import { useMemo } from 'react';

import { CGPSProducts } from '../types';

import { useUserInfo } from './use-user-info';

export type ProductKeys = keyof CGPSProducts | 'taxplan' | 'admin';

type ProductsAccess = Record<ProductKeys, boolean>;

export const useUserHasProductsAccess = (): ProductsAccess => {
  const user = useUserInfo();

  const hasTaxplanAccess: boolean = useMemo(
    () =>
      user?.products?.['taxplan']?.tiers
        ? Object.values(user.products['taxplan'].tiers).some(
            (tier) => tier.has_access,
          )
        : false,
    [user],
  );

  const hasCoreAccess: boolean = useMemo(
    () =>
      user?.products?.core?.tiers
        ? Object.values(user.products.core.tiers).some(
            (tier) => tier.has_access,
          )
        : false,
    [user],
  );

  const hasProgressAccess: boolean = useMemo(
    () =>
      user?.products?.progress?.tiers
        ? Object.values(user.products.progress.tiers).some(
            (tier) => tier.has_access,
          )
        : false,
    [user],
  );

  const hasEngageAccess: boolean = useMemo(
    () =>
      user?.products?.['engage']?.tiers
        ? Object.values(user.products['engage'].tiers).some(
            (tier) => tier.has_access,
          )
        : false,
    [user],
  );

  const hasProtectAccess: boolean = useMemo(
    () =>
      user?.products?.protect?.tiers
        ? Object.values(user.products.protect.tiers).some(
            (tier) => tier.has_access,
          )
        : false,
    [user],
  );

  const hasAdminAccess: boolean = useMemo(() => !!user?.internal_admin, [user]);

  return {
    taxplan: hasTaxplanAccess,
    core: hasCoreAccess,
    progress: hasProgressAccess,
    engage: hasEngageAccess,
    protect: hasProtectAccess,
    admin: hasAdminAccess,
  };
};
